import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable, from, of, throwError } from 'rxjs';
import { map, tap, timeout, concatMap, delay, materialize, dematerialize, retry, catchError, mergeMap, retryWhen } from 'rxjs/operators';

import { v4 as uuidv4 } from 'uuid';

import { environment } from '@environments/environment';
import Resources from '../constants/resourses';
import { SessionService } from './session.service';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { AlertDialogData } from '@shared/dialog/alert-dialog/alert-dialog.interface';
import { AlertDialogComponent } from '@shared/dialog/alert-dialog/alert-dialog.component';
import { AdImpressionRequest, ProductSubscriptionResponse, TenancyBannersListAPIResponse } from '@shared/models/ad-api-models';

@Injectable({
  providedIn: 'root'
})
export class RestapiService {
  offlineMessage: string = 'Ooops! You are not connected internet. Please check.';

  API_URL = environment.api_url;
  API_URL_METER = environment.api_url_meter;
  CONTACT_API_URL = environment.contact_api_url;
  EMAIL_API = environment.email_api;
  PAID_TO_SAVE_URL = environment.p2s_api;
  S3Url = environment.S3_url;
  LOGIN_API = environment.login_api;
  LAB_V1 = environment.lab_v1_url;
  ADS_API = environment.ads_url;
  PRODUCT_API = environment.product_url;
  TRANSACTION_API = environment.transaction_url;

  constructor(
    private http: HttpClient,
    private sessionService: SessionService,
    private dialog: MatDialog,
    private router: Router
  ) { }

  // Get API [energy]
  getAPI(cPath, extraHeaders?: { [header: string]: string }): Observable<any> {
    let httpOptions = {}
    if (cPath.split('?')[0] === 'suppliertariffs' || cPath.split('?')[0] === 'consent') {
      httpOptions = {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
          ...extraHeaders
        })
      };
    } else {
      httpOptions = {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
          ...extraHeaders
        })
      };
    }
    if (navigator.onLine) {
      return this.http.get(this.API_URL + cPath, httpOptions).pipe(
        timeout(30000),
        catchError((error) => {

          if (error?.error?.description === 'Please provide energy details for both electricity and gas' || error?.error?.description === 'Please provide energy details for electricity' || error?.error?.description === 'Please provide energy details for gas' || error?.error?.additionalAttributes?.retryRequired === 'N') {
            return throwError(error);
          } else {
            return this.http.get(this.API_URL + cPath, httpOptions);
          }
        }),
        map(this.responseMapper),
      );
    } else {
      window.alert(this.offlineMessage);
    }
  }

  getPostCodeAPI(cPath): Observable<any> {
    let httpOptions = {}

    httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    };
    if (navigator.onLine) {
      return this.http.get(this.API_URL + cPath, httpOptions).pipe(
        timeout(30000),
        catchError((error) => {
          if (error?.error?.errorCode === 'CTM_INTERNAL_CHECK_ISSUE' || error?.error?.status === 'CTM_INTERNAL_CHECK_ISSUE') {
            return throwError(error);
          } else {
            // retry the request only if the error code retry required is Y
            if (error?.error?.additionalAttributes?.retryRequired === 'N') {
              return throwError(error);
            } else {
              return this.http.get(this.API_URL + cPath, httpOptions).pipe(
                retry(1),
              );
            }
          }
        }),
        map(this.responseMapper)
      );
    } else {
      window.alert(this.offlineMessage);
      return throwError('You are offline');
    }
  }
  // Get S3 file
  getS3FileAPI(cPath): Observable<any> {
    if (navigator.onLine) {
      return this.http.get(this.S3Url + cPath, { responseType: 'text' });
    } else {
      window.alert(this.offlineMessage);
    }
  }

  // POST API [energy]
  postAPI(cPath, bodyObj): Observable<any> {
    let httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    };
    if (navigator.onLine) {
      return this.http.post<any>(this.API_URL + cPath, bodyObj, httpOptions).pipe(
        timeout(30000),
        retry(1),
        tap((data) => {
        }),
        map(this.responseMapper),
      );
    } else {
      window.alert(this.offlineMessage);
    }
  }

  // POST API [Login]
  postLoginAPI(cPath, bodyObj): Observable<any> {
    let httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      })
    };
    if (navigator.onLine) {
      return this.http.post<any>(this.LOGIN_API + cPath, bodyObj, httpOptions).pipe(
        timeout(30000),
        retry(1),
        tap((data) => {
        }),
        map(this.responseMapper),
      );
    } else {
      window.alert(this.offlineMessage);
    }
  }

  // POST API [meter]
  postNewAPI(cPath, bodyObj): Observable<any> {
    let httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    };
    if (navigator.onLine) {
      return this.http.post<any>(this.API_URL_METER + cPath, bodyObj, httpOptions).pipe(
        timeout(30000),
        retry(1),
        tap((data) => {
        }),
        map(this.responseMapper),
      );
    } else {
      window.alert(this.offlineMessage);
    }
  }

  // POST API [] - contact
  postcontactAPI(cPath, bodyObj): Observable<any> {
    let httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      })
    };
    if (navigator.onLine) {
      return this.http.post<any>(this.CONTACT_API_URL + cPath, bodyObj, httpOptions).pipe(
        timeout(30000),
        retry(1),
        tap((data) => {
        }),
        map(this.responseMapper),
      );
    } else {
      window.alert(this.offlineMessage);
    }
  }

  // POST API [meter] - [netzero]
  postNetZeroAPI(cPath, bodyObj): Observable<any> {
    let httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    };
    if (navigator.onLine) {
      return this.http.post<any>(this.API_URL_METER + cPath, bodyObj, httpOptions).pipe(
        timeout(30000),
        retry(1),
        tap((data) => {
        }),
        map(this.responseMapper),
      );
    } else {
      window.alert(this.offlineMessage);
    }
  }

  // POST API [p2s]
  postPTSApi(cPath, bodyObj): Observable<any> {
    let httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    };
    if (navigator.onLine) {
      return this.http.post<any>(this.PAID_TO_SAVE_URL + cPath, bodyObj, httpOptions).pipe(
        timeout(30000),
        retry(1),
        tap((data) => {
        }),
        map(this.responseMapper),
      );
    } else {
      window.alert(this.offlineMessage);
    }
  }

  // POST API [Labrador PROD]
  postLabAPI(cPath, bodyObj): Observable<any> {
    let httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    };
    if (navigator.onLine) {
      return this.http.post<any>(this.LAB_V1 + cPath, bodyObj, httpOptions).pipe(
        timeout(30000),
        retry(1),
        tap((data) => {
        }),
        map(this.responseMapper),
      );
    } else {
      window.alert(this.offlineMessage);
    }
  }

  // PUT API [energy]
  putAPI(cPath, bodyObj): Observable<any> {
    let httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    };
    if (navigator.onLine) {
      return this.http.put<any>(this.API_URL + cPath, bodyObj, httpOptions).pipe(
        retry(1),
        timeout(30000),
        catchError((error) => {
          // if address is not found then show popup and redirect to the home page
          if (error?.error?.msg?.description === 'Address Details Not found') {
            const alertDialogData: AlertDialogData = {
              pageName: 'Put current usage',
              title: 'Address not found',
              message: error?.error?.msg?.description,
              buttonText: 'Go to postcode page'
            };
            const dialogRef = this.dialog.open(AlertDialogComponent, {
              data: alertDialogData,
              maxWidth: window.innerWidth <= 767 ? '95vw' : '600px',
              disableClose: true,
              autoFocus: false,
              panelClass: 'ctm-modal'
            });

            dialogRef.afterClosed().subscribe(result => {
              if (!result) {
                // this.dialogRef.close(false);
                // Reset session storage
                this.sessionService.clearSessionStorage();
                this.sessionService.clearData();
                // Navigate to home page
                this.router.navigate(['/']);
              }
            });
            return throwError(error);
          }
          if (error?.error?.errorCode === 400) {
            return throwError(error);
          } else {
            console.log(error)
            return this.http.get(this.API_URL + cPath, httpOptions).pipe(
              retry(1),
            );
          }
        }),
        tap((data) => {
        }),
        map(this.responseMapper),
      );
    } else {
      window.alert(this.offlineMessage);
    }
  }

  // PATCH API [energy]
  patchAPI(cPath, bodyObj): Observable<any> {
    let httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    };
    if (navigator.onLine) {
      return this.http.patch<any>(this.API_URL + cPath, bodyObj, httpOptions).pipe(
        timeout(30000),
        retry(1),
        tap((data) => {
        }),
        map(this.responseMapper),
      );
    } else {
      window.alert(this.offlineMessage);
    }
  }

  deleteAPI(cPath: string, bodyObj: any): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    };

    if (navigator.onLine) {
      return this.http.delete<any>(`${this.API_URL}${cPath}`, { body: bodyObj, headers: httpOptions.headers })
        .pipe(
          timeout(30000),
          retry(1),
          tap((data) => {
            // You can perform actions on the response data here if needed
          }),
          map(this.responseMapper)
        );
    } else {
      window.alert(this.offlineMessage);
      return null; // You may want to handle this differently based on your use case
    }
  }

  deleteAPIs(cPath: string, bodyObj?: any): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      }),
      body: bodyObj
    };

    if (navigator.onLine) {
      return this.http.delete<any>(`${this.API_URL}${cPath}`, httpOptions)
        .pipe(
          timeout(30000),
          retry(1),
          tap((data) => {
            // You can perform actions on the response data here if needed
          }),
          map(this.responseMapper)
        );
    } else {
      window.alert(this.offlineMessage);
      return null; // You may want to handle this differently based on your use case
    }
  }

  // Response mapper
  responseMapper(res: Response) {
    let resObj: any = res;
    return resObj || {};
  }

  // is Logged in
  isLoggedIn(): string {
    return this.sessionService.getItemFromSession(Resources.SessionStorageKeys.AccessToken);
  }


  // Social related methods - facebook
  FBAuthenticate(accessToken) {
    return from(new Promise(resolve => {
      fetch(`https://graph.facebook.com/v12.0/me?fields=id,gender,name,email,birthday,first_name,middle_name,last_name&access_token=${accessToken}`)
        .then(response => resolve(response.json()));
    })).pipe(concatMap((data: any) => {

      if (data.error) return this.unauthorized(data.error.message);

      const account = {
        id: data.id,
        name: data.name,
        first_name: data.first_name,
        last_name: data.last_name,
        gender: data.gender,
        email: data.email,
        dob: data.birthday
      }

      return this.ok({
        ...account, token: this.generateJwtToken(account)
      });
    }));
  }

  generateJwtToken(account) {
    // create token that expires in 15 minutes
    const tokenPayload = {
      exp: Math.round(new Date(Date.now() + 15 * 60 * 1000).getTime() / 1000),
      id: account.id
    }
    return `fake-jwt-token.${btoa(JSON.stringify(tokenPayload))}`;
  }

  ok(body?) {
    return of(new HttpResponse({ status: 200, body }))
      .pipe(delay(500));
  }

  unauthorized(message = 'Unauthorized') {
    return throwError({ status: 401, error: { message } })
      .pipe(materialize(), delay(500), dematerialize());
  }

  // Transaction of product configuration
  postTransactionAPI(cPath, bodyObj): Observable<any> {
    let httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    };
    if (navigator.onLine) {
      return this.http.post<any>(this.API_URL + cPath, bodyObj, httpOptions).pipe(
        timeout(30000),
        catchError((error) => {
          if (error?.error?.additionalAttributes?.retryRequired === 'N') {
            return throwError(error);
          } else {
            return this.http.get(this.API_URL + cPath, httpOptions).pipe(
              retry(1),
            );
          }
        }),
        tap((data) => {
        }),
        map(this.responseMapper),
      );
    } else {
      window.alert(this.offlineMessage);
    }
  }

  // Get Supplier Config API
  getSupplierConfigAPI(path): Observable<any> {
    return this.http.get(path);
  }

  // On user boarding API
  onUserOnboarding(): void {
    const cPath = '/v1/users/onboard';
    const uuid = uuidv4();
    const bodyObj = {
      email: this.sessionService.getData(Resources.SessionStorageKeys.Email),
      correlationId: this.sessionService.getItemFromSession(Resources.SessionStorageKeys.CorrelationId),
      uuidToken: uuid
    };

    this.postAPI(cPath, bodyObj).subscribe({
      next: (response) => {
      }, error: (error) => {
        console.log('Onboarding API Error', error);
      }
    });
  }

  trackAdImpressions(trackAdImpressionsRequest: AdImpressionRequest): Observable<any> {
    const queryParams = new HttpParams()
      .set('productName', 'TENANCY_BANNER')
      .set('isSupplier', 'false')
      .set('elecSupplierId', 'ALL');

    return this.makeHttpRequest(
      this.http.get(this.API_URL + this.PRODUCT_API, { params: queryParams, ...this.getHttpHeaders() })
    ).pipe(
      mergeMap((response: ProductSubscriptionResponse) => {
        // Extract productSubscriptionId from the response
        const productSubscriptionId = response?.data?.product_subscription_id;
        trackAdImpressionsRequest.productSubscriptionId = productSubscriptionId;

        return this.makeHttpRequest(
          this.http.post(this.API_URL + this.TRANSACTION_API, trackAdImpressionsRequest, this.getHttpHeaders())
        );
      }),
      catchError((error) => {
        console.error('Error tracking ad impressions or completing transaction', error);
        return throwError(error);
      })
    )
  }

  // Get ADS List API 
  getAdsList(correlationId: string): Observable<TenancyBannersListAPIResponse> {
    return this.makeHttpRequest(this.http.post(this.API_URL + this.ADS_API + '/tenancy-banners', {
      correlationId
    }, {
      ...this.getHttpHeaders(),
    }));
  }

  private getHttpHeaders(contentType: string = 'application/json') {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': contentType
      })
    };
    return httpOptions;
  }

  private makeHttpRequest(httpObservable: Observable<any>) {
    if (navigator.onLine) {
      return httpObservable.pipe(
        timeout(30000),
        catchError((error) => {
          return throwError(error);
        }),
        map(this.responseMapper),
      );
    } else {
      window.alert(this.offlineMessage);
    }
  }
}

